import ThrottledApi from "services/api/throttledApi";
import { AxiosResponse } from "axios";
import { STORES_ENDPOINTS } from "constants/apiEndPoints";
import { StoreSummary } from "types";
import ExpiredStoreModel, { mapExpiredStoresToModels } from "models/store/ExpiredStoreModel";

export interface StoreSummaryDateFilters {
  startDate: string;
  endDate: string;
}

export interface StoreInvestigationGamePack {
  game_id: string;
  pack_id: string | null;
}

export interface StoreInvestigationRootCauseAudit {
  game_id: string;
  pack_id: string;
  audit_id: string;
  audit_at: string;
  operator_name?: string | null;
}

export interface StoreInvestigationIssue {
  type: string;
  game_packs: StoreInvestigationGamePack[];
  root_cause_audits?: StoreInvestigationRootCauseAudit[];
  impact: string;
}

export interface StoreInvestigationUnexplainedGame {
  type: 'unexplained_game_discrepancy';
  game_id: string;
  plu: number | null;
  pack_count: number;
  variance: {
    ticket_quantity: number;
    sales_amount: number;
    higher_in: 'sales_reported' | 'inventory_removed' | 'mixed';
  };
}

export interface StoreInvestigationReport {
  store_id: string;
  store_name: string;
  business_date: string;
  outcome: 'matched' | 'mismatched' | 'insufficient_data' | 'failed';
  summary: {
    identified_issue_count: number;
    unexplained_game_count: number;
    tickets_requiring_explanation: number;
    dollars_requiring_explanation: number;
  };
  identified_issues: StoreInvestigationIssue[];
  unexplained_games: StoreInvestigationUnexplainedGame[];
  errors: string[];
}

const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getDefaultStoreSummaryDateFilters = (): StoreSummaryDateFilters => {
  const endDate = new Date();
  const startDate = new Date(endDate);
  startDate.setDate(endDate.getDate() - 6);

  return {
    startDate: formatDate(startDate),
    endDate: formatDate(endDate),
  };
};

const parseStoreSummaryResponse = (response: AxiosResponse): StoreSummary[] => {
  if (response?.data?.stores && Array.isArray(response.data.stores)) {
    return response.data.stores;
  }

  if (response?.data?.success && response.data.data?.stores) {
    return response.data.data.stores;
  }

  return [];
};

const isAccessDeniedError = (error: unknown): boolean =>
  (error as { response?: { status?: number } })?.response?.status === 403;

export const getExpiredStores = async () => {
  try {
    const response = await ThrottledApi.get(
      `${STORES_ENDPOINTS.GET_EXPIRED_STORES}`,
      {}
    ) as AxiosResponse;

    if (Array.isArray(response?.data)) {
      return ExpiredStoreModel.sortByExpiration(mapExpiredStoresToModels(response.data));
    }

    if (response?.data?.stores && Array.isArray(response.data.stores)) {
      return ExpiredStoreModel.sortByExpiration(mapExpiredStoresToModels(response.data.stores));
    }

    return [];
  } catch (error) {
    console.error('StoresService: Failed to fetch expired stores:', error);
    throw error;
  }
};

export const getStoresReconciliationSummary = async (
  filters: StoreSummaryDateFilters = getDefaultStoreSummaryDateFilters()
): Promise<StoreSummary[]> => {
  try {
    const response = await ThrottledApi.get(
      STORES_ENDPOINTS.GET_STORES_RECONCILIATION_SUMMARY(filters.startDate, filters.endDate),
      {}
    ) as AxiosResponse;

    return parseStoreSummaryResponse(response);
  } catch (error) {
    if (isAccessDeniedError(error)) {
      return [];
    }

    console.error('StoresService: Failed to fetch reconciliation stores summary:', error);
    throw error;
  }
};

export const getStoresSummaryStoresOnly = async (
  filters: StoreSummaryDateFilters = getDefaultStoreSummaryDateFilters()
): Promise<StoreSummary[]> => {
  try {
    const response = await ThrottledApi.get(
      STORES_ENDPOINTS.GET_STORES_SUMMARY_STORES_ONLY(filters.startDate, filters.endDate),
      {}
    ) as AxiosResponse;

    return parseStoreSummaryResponse(response);
  } catch (error) {
    console.error('StoresService: Failed to fetch stores-only summary:', error);
    throw error;
  }
};

export const getStoreReconciliationInvestigation = async (
  storeId: string,
  businessDate: string,
  signal?: AbortSignal
): Promise<StoreInvestigationReport> => {
  const response = await ThrottledApi.get(
    STORES_ENDPOINTS.GET_STORE_RECONCILIATION_INVESTIGATION(storeId),
    {
      params: {
        business_date: businessDate,
        as_of: new Date().toISOString(),
        shadow_mode: true,
      },
      signal,
    }
  ) as AxiosResponse<StoreInvestigationReport>;

  return response.data;
};

export const storesService = {
  getStoresReconciliationSummary,
  getStoresSummaryStoresOnly,
  getStoreReconciliationInvestigation,
  getExpiredStores
};

export default storesService;
